import { useEffect, useState } from 'react';

import {
  setLocalStorage,
  getLocalStorage,
  setCookie,
} from '@zola-helpers/client/dist/es/util/storage';

import _sample from 'lodash/sample';

import {
  createFavoritePhoto,
  createFavoriteStorefront,
  deleteFavoritePhoto,
  deleteFavoriteStorefront,
  goToVendorFavoriteOnboarding,
} from '~/actions/favoriteActions';
import { addToast } from '~/actions/toastsActions';
import { AddToastProps, ToastTypes } from '~/actions/types/toastsActionTypes';
import WhiteHeartAnimation from '~/components/common/animations/lottie/whiteHeartAnimation.json';
import { useUserContext } from '~/contexts/UserContext';
import useFavorites from '~/hooks/useFavorites';
import { useUserContextWeddingLocation } from '~/hooks/useUserContextWeddingLocation';
import { useAppSelector, useAppDispatch, AppDispatch } from '~/reducers';
import { getFavoriteItem, getFavoriteStorefrontEntity } from '~/selectors/favoritesSelectors';
import { BoardSourceEnum, BoardWeddingPhotoView } from '~/types/responseTypes';
import { isInThePast } from '~/util/dateUtils';
import { TrackingProductLocation } from '~/util/trackingHelper';

import CircleFavButton from './buttonShapes/CircleFavButton';
import CircleZuiFavButton from './buttonShapes/CircleZuiFavButton';
import CircleZuiV3FavButton from './buttonShapes/CircleZuiV3FavButton';
import FullWidthFavButton from './buttonShapes/FullWidthFavButton';
import HeartFavButton from './buttonShapes/HeartFavButton';
import V3FavButton from './buttonShapes/v3FavButton';
import { FavButtonShapeProps, FavoriteButtonType, FavoriteButtonVariant } from './types';

const locationLocalStorageKey = 'marketplaceRealWeddingsFavorites/LocationModalShown';
const rwDataCookie = 'rwQuickOnboardData';

type FavoriteButtonProps = Omit<FavButtonShapeProps, 'type' | 'isFavorited' | 'onClick'> & {
  hideNonFavorited?: boolean;
  location?: TrackingProductLocation;
  onAuthCallback?: () => void;
  onCancelAuthCallback?: () => void;
  rwContext?: boolean;
  suppressToast?: boolean;
  uuid: string;
  variant?: FavoriteButtonVariant;
};

type TrackingProps = {
  tilePosition?: number;
  shelfCustomized?: boolean;
};

export type StorefrontFavoriteButtonProps = FavoriteButtonProps &
  TrackingProps & {
    afterFavoriteCallback?: () => void;
    showCustomInitialToasts?: boolean;
    vendorPhotoUuid?: string;
  };

export type LottieProps = {
  animationContainer?: Element | null;
  animationName?: string;
  destroy?: (name: string) => void;
  loadAnimation?: (arg: unknown) => void;
  play?: (name: string) => void;
};

type ShowVendorToastProps = {
  vendorFavoriteCount: number;
  uuid: string;
  vendorPhotoUuid?: string | null;
  showCustomInitialToasts?: boolean;
  location?: TrackingProductLocation;
};

export const showVendorToast = (props: ShowVendorToastProps) => {
  const { vendorFavoriteCount, uuid, vendorPhotoUuid, showCustomInitialToasts, location } = props;

  return (dispatch: AppDispatch) => {
    if (showCustomInitialToasts && vendorFavoriteCount === 0) {
      dispatch(
        addToast({
          toastType: ToastTypes.FIRST_VENDOR_FAVORITE_TOAST,
          props: {
            id: uuid,
            location,
          },
        })
      );
    } else if (showCustomInitialToasts && vendorFavoriteCount === 1) {
      dispatch(
        addToast({
          toastType: ToastTypes.SECOND_VENDOR_FAVORITE_TOAST,
          props: {
            id: uuid,
            location,
          },
        })
      );
    } else {
      dispatch(
        addToast({
          toastType: ToastTypes.VENDOR_SAVED,
          props: {
            id: uuid,
            photoUuid: vendorPhotoUuid,
            location,
          },
        })
      );
    }
  };
};

export type PhotoFavoriteButtonProps = FavoriteButtonProps & TrackingProps & LottieProps;

export const StorefrontFavoriteButton = (props: StorefrontFavoriteButtonProps) => {
  const {
    afterFavoriteCallback,
    showCustomInitialToasts,
    vendorPhotoUuid,
    tilePosition,
    shelfCustomized,
    ...favoriteButtonProps
  } = props;

  const { uuid, location, suppressToast = false } = favoriteButtonProps;

  const dispatch = useAppDispatch();

  const vendorFavoriteCount = useAppSelector(getFavoriteStorefrontEntity)?.allIds?.length || 0;

  const favoritesInfo = useFavorites();

  const createFavorite = () => {
    const createFavoriteParams = {
      boardUuid: favoritesInfo.favoritePhotoBoardUuid || 'ERROR',
      source: BoardSourceEnum.WEB,
      location,
      tilePosition,
      shelfCustomized,
    };

    dispatch(
      createFavoriteStorefront({
        storefrontUuid: uuid,
        ...createFavoriteParams,
      })
    )
      .then(() => {
        if (!suppressToast) {
          dispatch(
            showVendorToast({
              vendorFavoriteCount,
              uuid,
              vendorPhotoUuid,
              showCustomInitialToasts,
              location,
            })
          );
        }

        // special favorite treatment for vendor blender cards
        if (afterFavoriteCallback) {
          afterFavoriteCallback();
        }
      })
      .catch(() => null);
  };

  const deleteFavorite = (favoriteUuid: string) => {
    dispatch(
      deleteFavoriteStorefront({
        uuid: favoriteUuid,
        storefrontUuid: uuid,
        tilePosition,
        location,
        shelfCustomized,
      })
    ).catch(() => null);
  };

  return (
    <FavoriteButton
      type="VENDOR"
      {...favoriteButtonProps}
      createFavorite={createFavorite}
      deleteFavorite={deleteFavorite}
    />
  );
};

export const PhotoFavoriteButton = (props: PhotoFavoriteButtonProps) => {
  const dispatch = useAppDispatch();
  const userContext = useUserContext();
  const { answered: coupleHasLocation } = useUserContextWeddingLocation();

  const {
    loadAnimation,
    play,
    destroy,
    animationName,
    animationContainer,
    tilePosition,
    shelfCustomized,
    ...favoriteButtonProps
  } = props;

  const { uuid, location, suppressToast = false } = favoriteButtonProps;

  const favoritesInfo = useFavorites();

  const showPhotoToast = (results: BoardWeddingPhotoView | void) => {
    if (results && results.vendorCardViewList) {
      const vendorToasts: AddToastProps[] = [];
      results.vendorCardViewList
        .filter((v) => !!v.id) // excludes non-zola vendors
        .forEach((vendor) => {
          let locationString;
          if (userContext?.user_role_account_weddings?.length) {
            const wedding = userContext?.user_role_account_weddings[0];
            // only set if we have a city and state
            if (wedding.city) {
              locationString = `${wedding.city}, ${wedding.state_province}`;
            }
          }
          const sameLocation = vendor.matchWeddingLocation;
          const availableSameDate = vendor.displayWeddingDateAvailability;
          const locationModalShown = getLocalStorage(locationLocalStorageKey);
          let eventDate;
          if (userContext?.user_role_account_weddings?.length) {
            eventDate = userContext.user_role_account_weddings[0].event_date;
          }

          const eventDateHasPassed = eventDate && isInThePast(eventDate);

          if (!locationModalShown && !coupleHasLocation) {
            vendorToasts.push({
              toastType: ToastTypes.ASK_LOCATION,
              props: {
                id: `${uuid}---ask-location`,
                vendorName: vendor.vendorName,
                vendorSlug: vendor.storefrontSlug,
                vendorType: vendor.taxonomyNode?.key,
                locationName: locationString,
                vendorUuid: vendor.storefrontUuid,
                photoUuid: vendor.storefrontCoverId || uuid,
                location,
              },
            });
            setLocalStorage(locationLocalStorageKey, 'true');
          } else if (
            sameLocation &&
            availableSameDate &&
            locationString &&
            eventDate &&
            !eventDateHasPassed
          ) {
            // add full toast
            vendorToasts.push({
              toastType: ToastTypes.PHOTO_MATCH_DATE_LOCATION,
              props: {
                photoUuid: uuid,
                id: `${uuid}---${vendor.storefrontUuid}---full-match`,
                vendorName: vendor.vendorName,
                vendorSlug: vendor.storefrontSlug,
                vendorType: vendor.taxonomyNode?.key,
                vendorPhotoUuid: vendor.storefrontCoverId,
                couplePhotoUuid: uuid,
                vendorUuid: vendor.storefrontUuid,
                locationName: locationString,
                availableDate: eventDate, // get from usercontext
                location,
              },
            });
          } else if (sameLocation && locationString) {
            // add just location match toast
            vendorToasts.push({
              toastType: ToastTypes.PHOTO_MATCH_LOCATION,
              props: {
                vendorName: vendor.vendorName,
                vendorSlug: vendor.storefrontSlug,
                vendorType: vendor.taxonomyNode?.key,
                locationName: locationString,
                vendorUuid: vendor.storefrontUuid,
                photoUuid: vendor.storefrontCoverId || uuid,
                id: `${uuid}-${vendor.storefrontUuid}---location-match`,
                location,
              },
            });
          }
        });
      if (vendorToasts.length < 1) {
        dispatch(
          addToast({
            toastType: ToastTypes.PHOTO_SAVED,
            props: {
              photoUuid: uuid,
              id: `${uuid}---temp-toast-no-match---${Math.random()}`,
              isVendor: false,
              location,
            },
          })
        );
      } else {
        const makeAccountToasts = vendorToasts.filter(
          (x) => x.toastType === ToastTypes.MAKE_ACCOUNT
        );
        const askLocationToasts = vendorToasts.filter(
          (x) => x.toastType === ToastTypes.ASK_LOCATION
        );
        const fullMatchToasts = vendorToasts.filter(
          (x) => x.toastType === ToastTypes.PHOTO_MATCH_DATE_LOCATION
        );
        const fullMatchPhotographerToasts = fullMatchToasts.filter(
          (x) => x.props.vendorType === 'wedding-photographers'
        );
        const partialMatchToasts = vendorToasts.filter(
          (x) => x.toastType === ToastTypes.PHOTO_MATCH_LOCATION
        );
        const partialMatchPhotographerToasts = partialMatchToasts.filter(
          (x) => x.props.vendorType === 'wedding-photographers'
        );
        /**
         * in descending priority, attempt to:
         * -1. show make account toast
         *  0. show ask location toast
         *  1. show a random photographer matching location and date
         *  2. show a random vendor matching location and date
         *  3. show a random photographer matching location
         *  4. show a random vendor matching location
         *  5. show the standard “photo added” toast
         */
        if (makeAccountToasts.length) {
          dispatch(addToast(makeAccountToasts[0]));
        } else if (askLocationToasts.length) {
          dispatch(addToast(askLocationToasts[0]));
        } else if (fullMatchPhotographerToasts.length) {
          dispatch(addToast(_sample(fullMatchPhotographerToasts) as AddToastProps));
        } else if (fullMatchToasts.length) {
          dispatch(addToast(_sample(fullMatchToasts) as AddToastProps));
        } else if (partialMatchPhotographerToasts.length) {
          dispatch(addToast(_sample(partialMatchPhotographerToasts) as AddToastProps));
        } else if (partialMatchToasts.length) {
          dispatch(addToast(_sample(partialMatchToasts) as AddToastProps));
        }
      }
    }
  };

  const createFavorite = () => {
    // same boardUuid for storefronts & photos
    const createFavoriteParams = {
      boardUuid: favoritesInfo.favoritePhotoBoardUuid || 'ERROR',
      source: BoardSourceEnum.WEB,
      location,
      tilePosition,
      shelfCustomized,
    };

    dispatch(
      createFavoritePhoto({
        imageId: uuid,
        ...createFavoriteParams,
      })
    )
      .then((results: BoardWeddingPhotoView | void) => {
        if (!suppressToast) {
          showPhotoToast(results);
        }

        // currently only instance is mobile rw landing page
        if (play && loadAnimation && destroy && animationName) {
          loadAnimation({
            container: animationContainer,
            loop: false,
            animationData: WhiteHeartAnimation,
            renderer: 'svg',
            name: animationName,
          });
          play(animationName);
          setTimeout(() => destroy(animationName), 1000);
        }
      })
      .catch(() => null);
  };

  const deleteFavorite = (favoriteUuid: string) => {
    dispatch(
      deleteFavoritePhoto({
        uuid: favoriteUuid,
        imageId: uuid,
        tilePosition,
        location,
        shelfCustomized,
      })
    ).catch(() => null);
  };

  const createFavoritePhotoPayload = {
    imageId: uuid,
    boardUuid: favoritesInfo.favoritePhotoBoardUuid || 'ERROR',
    source: BoardSourceEnum.WEB,
    location,
    tilePosition,
    shelfCustomized,
  };

  return (
    <FavoriteButton
      type="PHOTO"
      {...favoriteButtonProps}
      createFavorite={createFavorite}
      deleteFavorite={deleteFavorite}
      createFavoritePhotoPayload={createFavoritePhotoPayload}
    />
  );
};

type InternalFavoriteButtonProps = Omit<FavoriteButtonProps, 'tilePosition' | 'shelfCustomized'> & {
  createFavorite: () => void;
  deleteFavorite: (favoriteUuid: string) => void;
  type: FavoriteButtonType;
  createFavoritePhotoPayload?: {
    imageId: string;
    boardUuid: string;
    source: BoardSourceEnum;
    location: TrackingProductLocation | undefined;
    tilePosition: number | undefined;
    shelfCustomized: boolean | undefined;
  };
};

const FavoriteButton = ({
  buttonClassOverrides,
  className,
  createFavorite,
  deleteFavorite,
  hideNonFavorited = false,
  imageClassOverrides,
  location,
  onMouseEnter = () => null,
  onMouseLeave = () => null,
  type,
  uuid,
  variant = 'compact',
  whiteOutlineDefault = false,
  createFavoritePhotoPayload,
}: InternalFavoriteButtonProps): JSX.Element | null => {
  const [isFavorited, setIsFavorited] = useState(false);
  const userContext = useUserContext();
  const isVendor = type === 'VENDOR';
  const selector = getFavoriteItem({ imageId: uuid, storefrontUuid: uuid }, isVendor);
  const favoriteItem = useAppSelector(selector);

  useEffect(() => {
    setIsFavorited(!!favoriteItem);
  }, [favoriteItem]);

  const favorite = () => {
    if (userContext?.is_guest) {
      if (!isVendor) {
        const rwQuickOnboardData = {
          origin: location,
          returnURL: window.location.href,
          photoId: uuid,
          createFavoritePhotoPayload,
        };
        setCookie(rwDataCookie, JSON.stringify(rwQuickOnboardData)); // Set required data for RW Quick Onboarding
        window.location.assign('/wedding/onboard/real-weddings-favorite');
      } else {
        goToVendorFavoriteOnboarding(uuid);
      }
    } else {
      createFavorite();
    }
  };

  const unfavorite = () => {
    if (favoriteItem && 'uuid' in favoriteItem) {
      const { uuid: favoriteUuid } = favoriteItem;
      if (favoriteUuid) {
        deleteFavorite(favoriteUuid as string);
      }
    }
  };

  const clickHandler = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (isFavorited) {
      unfavorite();
    } else {
      favorite();
    }

    if (userContext?.is_guest) {
      setIsFavorited(false);
    } else {
      setIsFavorited(!isFavorited);
    }
  };

  const favButtonShapeProps: FavButtonShapeProps = {
    onMouseEnter,
    onMouseLeave,
    isFavorited,
    onClick: clickHandler,
    buttonClassOverrides,
    className,
    type,
    whiteOutlineDefault,
    imageClassOverrides,
  };

  if (hideNonFavorited && !isFavorited) {
    return null;
  }

  // TODO: Clean up this absolute mess of variants

  if (variant === 'full') {
    return <FullWidthFavButton {...favButtonShapeProps} />;
  }

  if (variant === 'circle') {
    return <CircleFavButton {...favButtonShapeProps} />;
  }

  if (variant === 'circleZui') {
    return <CircleZuiFavButton {...favButtonShapeProps} />;
  }

  if (variant === 'circleZuiV3') {
    return <CircleZuiV3FavButton {...favButtonShapeProps} />;
  }

  if (variant === 'v3') {
    return <V3FavButton {...favButtonShapeProps} />;
  }

  return <HeartFavButton {...favButtonShapeProps} isCircleOutline={variant === 'circleOutline'} />;
};
